import React, { useState, useEffect, useRef } from 'react';

const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  style = {}, 
  loading = 'lazy',
  ...props 
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  // Convert image path to WebP version
  const getWebPSrc = (originalSrc) => {
    if (!originalSrc) return null;
    const lastDot = originalSrc.lastIndexOf('.');
    if (lastDot === -1) return originalSrc;
    return originalSrc.substring(0, lastDot) + '.webp';
  };

  const getOptimizedSrc = React.useCallback(() => {
    if (!src) return null;
    
    // If it's an external URL, return as is
    if (src.startsWith('http') || src.startsWith('//')) {
      return src;
    }
    
    // For local images, try WebP first
    return getWebPSrc(src);
  }, [src]);

  const handleLoad = () => {
    setHasLoaded(true);
  };

  const handleError = () => {
    // If WebP fails, fallback to original image
    if (!hasError && imageSrc.endsWith('.webp')) {
      setHasError(true);
      setImageSrc(src);
    }
  };

  useEffect(() => {
    setImageSrc(getOptimizedSrc());
    setHasLoaded(false);
    setHasError(false);
  }, [src, getOptimizedSrc]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (loading === 'lazy' && imgRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting) {
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );

      observer.observe(imgRef.current);
      return () => observer.disconnect();
    }
  }, [loading]);

  // Don't render anything if no src is provided
  if (!src || !imageSrc) {
    return null;
  }

  return (
    <picture>
      {/* WebP source for modern browsers */}
      {!src?.startsWith('http') && src && (
        <source srcSet={getWebPSrc(src)} type="image/webp" />
      )}
      
      <img
        ref={imgRef}
        src={imageSrc}
        alt={alt}
        className={`optimized-image ${className} ${!hasLoaded ? 'loading' : ''}`}
        style={{
          transition: 'opacity 0.3s ease',
          opacity: hasLoaded ? 1 : 0.8,
          ...style
        }}
        loading={loading}
        onLoad={handleLoad}
        onError={handleError}
        {...props}
      />
    </picture>
  );
};

export default OptimizedImage;
